import { type RefObject, useEffect, useRef, useState } from "react"

type Edges = { start: boolean; end: boolean }

// Tells whether a horizontally scrolling element hides content before (start) or after (end) its
// visible part, kept up to date on scroll and resize. Also brings the active child (matching
// `activeSelector`) into view, so the current tab is never left off-screen.
export function useScrollEdges<T extends HTMLElement>(
  activeSelector?: string
): Edges & { ref: RefObject<T> } {
  const ref = useRef<T>(null)
  const [edges, setEdges] = useState<Edges>({ start: false, end: false })

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const update = () => {
      const hidden = element.scrollWidth - element.clientWidth
      const next = { start: element.scrollLeft > 1, end: element.scrollLeft < hidden - 1 }
      setEdges(previous =>
        previous.start === next.start && previous.end === next.end ? previous : next
      )
    }

    update()
    element.addEventListener("scroll", update, { passive: true })
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(update)
    resizeObserver?.observe(element)
    return () => {
      element.removeEventListener("scroll", update)
      resizeObserver?.disconnect()
    }
  }, [])

  // After a render, only when the active child changed (tab selected, route changed): revealing
  // it on every render pulled the list back while the user scrolled, each scroll re-rendering.
  const revealed = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const element = ref.current
    const active = activeSelector ? element?.querySelector<HTMLElement>(activeSelector) : null
    if (!element || !active || active === revealed.current) return
    revealed.current = active

    // Position of the active child within the scrolled content.
    const left =
      active.getBoundingClientRect().left -
      element.getBoundingClientRect().left +
      element.scrollLeft
    const right = left + active.getBoundingClientRect().width
    if (left < element.scrollLeft) element.scrollLeft = left
    else if (right > element.scrollLeft + element.clientWidth)
      element.scrollLeft = right - element.clientWidth
  })

  return { ref, ...edges }
}

const FADE = "40px"

// Mask fading the edges that hide content, so a cut-off tab reads as "more this way".
export const scrollFadeMask = ({ start, end }: Edges) => {
  if (!start && !end) return {}
  const mask = `linear-gradient(to right, ${start ? "transparent" : "black"} 0, black ${FADE}, black calc(100% - ${FADE}), ${end ? "transparent" : "black"} 100%)`
  return { maskImage: mask, WebkitMaskImage: mask }
}
